import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, errMessage } from "../api";
import {
  Rule,
  Footer,
  Nav,
  Verse,
  useBodyClass,
  useDocumentTitle,
} from "../components/Chrome";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const INTENTS = [
  {
    key: "maker",
    label: "I make things.",
    help: "I'd like to come, show work, or find my people.",
    blurb:
      "Whatever you make, whatever stage you're at. You don't need a portfolio and you don't need to be sure you count.",
    testid: "join-intent-maker-radio",
  },
  {
    key: "collaborate",
    label: "I'd like to host, commission or collaborate.",
    help: "A venue, a budget, an idea, or a question.",
    blurb:
      "If you're a pastor, a venue, or someone with a project in mind, this is for you. We'd be glad to make something \u2014 and we'd expect to be paid fairly for it.",
    testid: "join-intent-collab-radio",
  },
];

export default function Join() {
  useBodyClass("inner");
  useDocumentTitle(
    "Come to something",
    "Tell Sch\u0113ma you'd like to come. There's nothing to sign and no application.",
  );

  const [intent, setIntent] = useState("maker");
  const [form, setForm] = useState({
    name: "",
    email: "",
    city: "",
    makes: "",
    message: "",
  });
  const [subscribe, setSubscribe] = useState(true);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState("");

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((x) => ({ ...x, [k]: undefined }));
  };

  const active = INTENTS.find((i) => i.key === intent);

  const submit = async (e) => {
    e.preventDefault();
    setFailure("");
    const errs = {};
    if (!form.name.trim()) errs.name = "This needs an answer.";
    if (!EMAIL.test(form.email.trim()))
      errs.email = "That email doesn't look quite right.";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSending(true);
    try {
      await api.post("/inquiries", {
        intent,
        name: form.name.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        makes: form.makes.trim(),
        message: form.message.trim(),
        subscribe,
      });
      setSent(true);
    } catch (err) {
      setFailure(errMessage(err, "We couldn't send that just now. Try again?"));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Nav />
      <header className="page-head" data-testid="join-page-head">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>The entry process</span>
          </p>
          <h1>Come to something.</h1>
          <p className="standfirst">
            That&rsquo;s it. That&rsquo;s the whole process. There&rsquo;s nothing
            to sign, no application, and no portfolio. Come once, see whether the
            room is any good, come again.
          </p>
        </div>
      </header>

      <section data-testid="join-who">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>Who this is for</span>
          </p>
          <h2>Most artists, most of the time.</h2>
          <p>
            Artists who confess Christ and are somewhere in the long middle of
            learning a craft &mdash; which is most artists, most of the time. If
            you&rsquo;re not sure you count as an artist, you probably do, and
            this is a decent place to find out. If you&rsquo;ve been at it twenty
            years, we need you more than you need us, and we&rsquo;ll try to be
            worth your time.
          </p>
          <p>
            Not sure what an evening actually looks like?{" "}
            <Link to="/visit">Read what you&rsquo;d be walking into</Link>.
          </p>
        </div>
      </section>

      <section id="come" data-testid="join-form-section">
        <div className="form-shell" style={{ padding: "0 1.5rem" }}>
          {sent ? (
            <div className="thanks" data-testid="join-success">
              <div className="art" aria-hidden="true" />
              <h3>Received.</h3>
              <p>We&rsquo;ll reply like humans. Give us a few days.</p>
              <p style={{ marginTop: "1.6rem" }}>
                <Link to="/gatherings" className="linkish" data-testid="join-success-gatherings-link">
                  See what&rsquo;s on &rarr;
                </Link>
              </p>
            </div>
          ) : (
            <>
              <p className="eyebrow sc">
                <span className="num">&middot;</span>
                <span>Get in touch</span>
              </p>
              <h2>Tell us you&rsquo;d like to come.</h2>
              <p className="path-blurb">
                We&rsquo;ll write back and tell you when the next thing is. The
                first circles are forming in Calgary.
              </p>

              <div className="paths" role="group" aria-label="Why you're writing">
                {INTENTS.map((i) => (
                  <button
                    key={i.key}
                    type="button"
                    className="path-btn"
                    aria-pressed={intent === i.key}
                    onClick={() => setIntent(i.key)}
                    data-testid={i.testid}
                  >
                    {i.label}
                  </button>
                ))}
              </div>

              <p className="path-blurb" data-testid="join-intent-blurb">
                {active.blurb}
              </p>

              <form onSubmit={submit} noValidate>
                <div className="hp" aria-hidden="true">
                  <label htmlFor="join-hp">Leave this empty</label>
                  <input id="join-hp" tabIndex={-1} autoComplete="off" />
                </div>

                <div className="row">
                  <div className="field">
                    <label htmlFor="join-name">Your name</label>
                    <input
                      id="join-name"
                      value={form.name}
                      onChange={set("name")}
                      autoComplete="name"
                      aria-invalid={errors.name ? "true" : "false"}
                      data-testid="join-name-input"
                    />
                    {errors.name ? (
                      <span className="err" data-testid="join-name-error">
                        <span className="lab">Note</span>
                        {errors.name}
                      </span>
                    ) : null}
                  </div>
                  <div className="field">
                    <label htmlFor="join-email">Email</label>
                    <input
                      id="join-email"
                      type="email"
                      inputMode="email"
                      value={form.email}
                      onChange={set("email")}
                      autoComplete="email"
                      aria-invalid={errors.email ? "true" : "false"}
                      data-testid="join-email-input"
                    />
                    {errors.email ? (
                      <span className="err" data-testid="join-email-error">
                        <span className="lab">Note</span>
                        {errors.email}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="join-city">
                    City or neighbourhood{" "}
                    <span className="opt">&mdash; optional</span>
                  </label>
                  <input
                    id="join-city"
                    value={form.city}
                    onChange={set("city")}
                    data-testid="join-city-input"
                  />
                </div>

                <div className="field">
                  <label htmlFor="join-makes">
                    {intent === "maker"
                      ? "What you make \u2014 or want to"
                      : "What you have in mind"}{" "}
                    <span className="opt">&mdash; optional</span>
                  </label>
                  <input
                    id="join-makes"
                    value={form.makes}
                    onChange={set("makes")}
                    data-testid="join-makes-input"
                  />
                </div>

                <div className="field">
                  <label htmlFor="join-message">
                    Anything you&rsquo;d like to say{" "}
                    <span className="opt">&mdash; optional</span>
                  </label>
                  <textarea
                    id="join-message"
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    data-testid="join-message-textarea"
                  />
                </div>

                <div className="check">
                  <input
                    id="join-subscribe"
                    type="checkbox"
                    checked={subscribe}
                    onChange={(e) => setSubscribe(e.target.checked)}
                    data-testid="join-subscribe-checkbox"
                  />
                  <div>
                    <label htmlFor="join-subscribe">
                      Write to me when something is on.
                    </label>
                    <span className="hint">
                      One email when there&rsquo;s a date worth turning up to. No
                      drip campaigns.
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="send"
                  disabled={sending}
                  data-testid="join-submit-button"
                >
                  {sending ? "Sending\u2026" : "Send it"}
                </button>

                {failure ? (
                  <div className="form-error" data-testid="join-submit-error">
                    {failure}
                  </div>
                ) : null}

                <p className="form-note">
                  Goes straight to Austin. No list, no automated anything &mdash;
                  just a reply from a person.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      <Rule />

      <section data-testid="join-door">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>One more thing</span>
          </p>
          <h2>The door is the point.</h2>
          <p>
            We&rsquo;re not recruiting you away from anywhere. Whatever
            congregation you belong to, keep belonging to it &mdash; this is the
            workshop, not the sanctuary.
          </p>
        </div>
      </section>

      <Verse />
      <Footer />
    </>
  );
}
